import { motion } from "framer-motion";
import { Mail, Linkedin, Instagram, Phone, ArrowUpRight } from "lucide-react";

const EASE = [0.83, 0, 0.17, 1];

const socials = [
  {
    name: "Email",
    handle: "fyapropertygroup@gmail.com",
    href: "mailto:fyapropertygroup@gmail.com",
    icon: Mail,
    testId: "social-email",
  },
  {
    name: "LinkedIn",
    handle: "/in/fatimabda",
    href: "https://www.linkedin.com/in/fatimabda",
    icon: Linkedin,
    testId: "social-linkedin",
  },
  {
    name: "Instagram",
    handle: "@fatimaabdallah.co",
    href: "https://www.instagram.com/fatimaabdallah.co",
    icon: Instagram,
    testId: "social-instagram",
  },
];

export default function FinalCTA() {
  return (
    <section
      id="connect"
      data-testid="connect-section"
      className="relative bg-plum py-32 md:py-48 overflow-hidden border-t border-gold/10"
    >
      {/* Ambient background */}
      <div className="absolute inset-0 -z-10" aria-hidden>
        <div
          className="absolute inset-0 bg-cover bg-center image-duotone opacity-[0.12]"
          style={{
            backgroundImage:
              'url("https://images.unsplash.com/photo-1613968317130-e6565f71b9d3?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400")',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-plum via-plum/70 to-plum" />
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.2, ease: EASE }}
          className="max-w-5xl"
        >
          <div className="flex items-center gap-3 text-gold text-[11px] tracking-[0.35em] uppercase">
            <span className="w-8 h-px bg-gold" />
            The Invitation
          </div>

          <h2
            data-testid="final-cta-headline"
            className="mt-8 font-serif font-light text-cream text-5xl sm:text-6xl md:text-7xl lg:text-[112px] leading-[0.92] tracking-tighter"
          >
            Let's start a
            <br />
            <span className="italic text-gold">conversation.</span>
          </h2>

          <p className="mt-10 text-cream/75 text-lg md:text-xl leading-relaxed max-w-2xl">
            Whether you're exploring property investment, land opportunities,
            SDA developments or strategic partnerships, I'd love to connect.
          </p>

          <div className="mt-12 flex flex-wrap items-center gap-4 md:gap-5">
            <a
              href="mailto:fyapropertygroup@gmail.com"
              data-testid="final-cta-email-btn"
              className="group inline-flex items-center gap-3 px-8 md:px-10 py-5 bg-gold text-plum text-xs uppercase tracking-[0.3em] font-medium hover:bg-cream transition-colors duration-500"
            >
              <Mail size={16} strokeWidth={1.5} />
              Email Fatima
              <ArrowUpRight
                size={16}
                className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
            <a
              href="tel:+61422011598"
              data-testid="final-cta-call-btn"
              className="group inline-flex items-center gap-3 px-8 md:px-10 py-5 border border-gold text-cream text-xs uppercase tracking-[0.3em] font-medium hover:bg-gold hover:text-plum transition-colors duration-500"
            >
              <Phone size={16} strokeWidth={1.5} />
              Call Fatima
              <ArrowUpRight
                size={16}
                className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-2 text-cream/60 text-sm tracking-[0.12em]">
            <a
              href="mailto:fyapropertygroup@gmail.com"
              data-testid="final-cta-email-link"
              className="hover:text-gold link-underline transition-colors"
            >
              fyapropertygroup@gmail.com
            </a>
            <span className="hidden md:inline text-gold/40">·</span>
            <a
              href="tel:+61422011598"
              data-testid="final-cta-phone-link"
              className="hover:text-gold link-underline transition-colors"
            >
              0422 011 598
            </a>
          </div>
        </motion.div>

        {/* Socials row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: EASE, delay: 0.2 }}
          className="mt-24 md:mt-32 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-4xl"
        >
          {socials.map((s) => {
            const Icon = s.icon;
            return (
              <a
                key={s.name}
                href={s.href}
                data-testid={s.testId}
                target={s.name === "Email" ? "_self" : "_blank"}
                rel="noreferrer"
                className="group relative p-6 md:p-8 border border-gold/20 hover:border-gold/70 transition-colors duration-500"
              >
                <div className="flex items-center justify-between mb-8">
                  <Icon
                    size={20}
                    strokeWidth={1.25}
                    className="text-gold"
                  />
                  <ArrowUpRight
                    size={16}
                    className="text-cream/40 group-hover:text-gold transition-all duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  />
                </div>
                <div className="text-[10px] tracking-[0.35em] uppercase text-cream/50">
                  {s.name}
                </div>
                <div className="mt-2 font-serif text-2xl text-cream group-hover:text-gold transition-colors duration-500">
                  {s.handle}
                </div>
              </a>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
